import React, { useCallback, useState } from 'react';

import { useLiveQuery } from 'dexie-react-hooks';
import { ulid } from 'ulid';

import { apiPost } from '../lib/api_v2';
import db, { DEXIE_IS_LOADING } from '../lib/db';
import { useWorkspaceIdScope } from '../lib/workspaceIdScope';

import { KEYCHAIN } from '../contexts/KeyMaster';
import LocalTime from './messages/LocalTime';
import DebugDumpModal from './modals/DebugDumpModal';
import { ModalPortal, useRetainedModalValue } from './modals/ModalPortal';

const formatDuration = (duration) => {
  // duration provided in milliseconds
  const total_seconds = Math.max(0, Math.round(duration / 1000));
  const days = Math.floor(total_seconds / 86400);
  const hours = Math.floor((total_seconds % 86400) / 3600);
  const minutes = Math.floor((total_seconds % 3600) / 60);
  const seconds = total_seconds % 60;

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (days === 0 && seconds > 0) parts.push(`${seconds}s`);

  return parts.join(' ');
};

const CATCHUP_WINDOW_MS = 500 * 60 * 60 * 1000; // 500 hours

const CatchupSimulator = () => {
  const workspaceId = useWorkspaceIdScope();
  const workspace = useLiveQuery(() => (workspaceId ? db.workspaces.get(workspaceId) : null), [workspaceId], DEXIE_IS_LOADING);
  const mainChannelId = workspace === DEXIE_IS_LOADING ? null : workspace?.main_channel_id || null;
  const [running, setRunning] = useState(false);

  const handleSimulate = useCallback(async () => {
    if (!mainChannelId || running) return;
    setRunning(true);
    try {
      const sinceMs = Date.now() - CATCHUP_WINDOW_MS;
      const sinceUlid = ulid(sinceMs);

      // wipe messages from Dexie so the chat goes empty for this channel
      const deleted = await db.messages
        .where('[channel_id+sort_order]')
        .between([mainChannelId, sinceUlid], [mainChannelId, '\uffff'], true, true)
        .delete();
      console.log(`[catchup-sim] Deleted ${deleted} messages from main channel ${mainChannelId} (last 500 hours)`);

      // inject a ready envelope so the catchup processor re-fetches journal entries for this window
      await db._catchup_envelopes.add({
        status: 'ready',
        since: sinceMs,
        until: Date.now(),
      });
      console.log('[catchup-sim] Envelope injected — catchup will process shortly');
    } catch (err) {
      console.error('[catchup-sim] Simulation failed', err);
    } finally {
      setRunning(false);
    }
  }, [mainChannelId, running]);

  return (
    <div className="flex items-center justify-between border-b border-gray-200 p-2 text-3xs" data-app-region="none">
      <span className="mr-1 text-gray-600">Main channel: {mainChannelId || '—'}</span>
      <button
        onClick={handleSimulate}
        disabled={!mainChannelId || running}
        className="rounded bg-red-500 px-2 py-0.5 text-3xs text-white hover:bg-red-600 disabled:opacity-40"
      >
        {running ? 'Triggering...' : 'Trigger Catchup'}
      </button>
    </div>
  );
};

const LogViewerCatchupBrowser = () => {
  const catchup_envelopes = useLiveQuery(() => db._catchup_envelopes.orderBy('id').reverse().limit(250).toArray(), [], DEXIE_IS_LOADING);
  const [debugDumpData, setDebugDumpData] = useState(null);
  const retainedDebugDumpData = useRetainedModalValue(debugDumpData);

  const handleCloseDebugDump = useCallback(() => setDebugDumpData(null), []);

  const fetchEnvelope = useCallback(async (since, until) => {
    const key_entries = (window.livejournal?.manifest || [])
      .filter((c) => c.workspace_id && c.key)
      .map((c) => ({ workspace_id: c.workspace_id, key: c.key }));
    const response = await apiPost('/journals/fetch', {
      since: Math.floor(since * 0.001),
      until: Math.floor(until * 0.001),
      key_entries,
    });
    const data = await response.json();
    setDebugDumpData({ title: 'Journal Entries', data: data?.data });
  }, []);

  const reprocessEnvelope = useCallback(async (envelope) => {
    if (!envelope) return;
    await db._catchup_envelopes.add({
      status: 'ready',
      since: envelope.since,
      until: envelope.until,
      original_id: envelope.original_id || envelope.id,
    });
  }, []);

  if (catchup_envelopes === DEXIE_IS_LOADING) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex flex-1 items-center justify-center">
          <div className="flex items-center gap-2">
            <div className="size-2 animate-bounce rounded-full bg-gray-300"></div>
            <div className="size-2 animate-bounce rounded-full bg-gray-300 [animation-delay:0.2s]"></div>
            <div className="size-2 animate-bounce rounded-full bg-gray-300 [animation-delay:0.4s]"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <CatchupSimulator />
      <div className="min-h-0 flex-1 overflow-y-auto">
        <table className="w-full text-3xs">
          <colgroup>
            <col />
            <col />
            <col />
            <col />
            <col />
            <col />
            <col />
          </colgroup>
          <thead>
            <tr className="bg-gray-100 [&>th]:p-1 [&>th]:text-left [&>th]:font-normal">
              <th className="pl-2">ID</th>
              <th>Status</th>
              <th>Since</th>
              <th>Until</th>
              <th>Try</th>
              <th colSpan={2} className="pr-2">
                Duration
              </th>
            </tr>
          </thead>
          <tbody>
            {catchup_envelopes.map((envelope) => (
              <React.Fragment key={envelope.id}>
                <tr className="odd:bg-gray-50 [&>td]:p-1 [&>td]:whitespace-nowrap [&>td:first-child]:pl-2">
                  <td>
                    {envelope.status === 'error' && (
                      <div onClick={() => reprocessEnvelope(envelope)} className="cursor-pointer">
                        {envelope.id}
                      </div>
                    )}
                    {envelope.status !== 'error' && envelope.id}
                  </td>
                  <td>{envelope.status}</td>
                  <td>
                    {envelope.since && (
                      <LocalTime key={`${envelope.id}-since-${envelope.since}`} timestamp={envelope.since * 0.001} format={'%H:%M:%S'} />
                    )}
                    {!envelope.since && <span className="text-red-700">[since]</span>}
                  </td>
                  <td>
                    {envelope.until && (
                      <LocalTime key={`${envelope.id}-until-${envelope.until}`} timestamp={envelope.until * 0.001} format={'%H:%M:%S'} />
                    )}
                    {!envelope.until && <span className="text-neutral-500">open</span>}
                  </td>
                  <td>
                    {envelope.attempts || 1} {envelope.original_id && `(${envelope.original_id})`}
                  </td>
                  <td>{envelope.since && envelope.until && formatDuration(envelope.until - envelope.since)}</td>
                  <td>
                    {envelope.since && envelope.until && (
                      <div
                        onClick={() => fetchEnvelope(envelope.since, envelope.until)}
                        className="cursor-pointer rounded-xs bg-gray-100 px-1 py-0.5 text-center text-3xs text-black hover:bg-gray-200"
                      >
                        {Object.hasOwn(envelope, 'total_entries') ? envelope.total_entries : '?'}
                      </div>
                    )}
                  </td>
                </tr>
                {(envelope.error || envelope.retry_reason) && (
                  <tr>
                    <td colSpan={7}>
                      <div
                        className={`px-2 py-1 text-3xs ${envelope.status === 'error' ? 'bg-red-50 text-red-700' : 'bg-yellow-50 text-yellow-700'}`}
                      >
                        {envelope.error && JSON.stringify(envelope.error)}
                        {envelope.retry_reason && (
                          <>
                            {envelope.error && <br />}
                            {` (retry reason: ${envelope.retry_reason})`}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <ModalPortal
        open={Boolean(debugDumpData)}
        onClose={handleCloseDebugDump}
        size="mini"
        keychainId={KEYCHAIN.MODAL__DEBUG_DUMP}
      >
        {retainedDebugDumpData ? (
          <DebugDumpModal title={retainedDebugDumpData.title} data={retainedDebugDumpData.data} onClose={handleCloseDebugDump} />
        ) : null}
      </ModalPortal>
    </div>
  );
};

export default LogViewerCatchupBrowser;
